;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"60639eb45a7f2d0500f93c46c1810f93baf92d8d"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";import * as Sentry from '@sentry/nextjs'

const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN
const SENTRY_RELEASE =
  process.env.NEXT_PUBLIC_SENTRY_RELEASE ??
  process.env.VERCEL_GIT_COMMIT_SHA ??
  process.env.npm_package_version ??
  'unknown'

const SENTRY_REDACT_KEYS = [
  'authorization',
  'cookie',
  'set-cookie',
  'token',
  'access_token',
  'refresh_token',
  'api_key',
  'apikey',
  'apiKey',
  'password',
  'secret',
  'session',
  'email',
  'phone',
]

const shouldRedactKey = (key: string): boolean => {
  const normalized = key.toLowerCase()
  return SENTRY_REDACT_KEYS.some((token) => normalized.includes(token))
}

const scrubValue = (value: unknown, depth = 0): unknown => {
  if (depth > 6) return value
  if (Array.isArray(value)) {
    return value.map((entry) => scrubValue(entry, depth + 1))
  }
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      if (shouldRedactKey(key)) {
        result[key] = '[REDACTED]'
        continue
      }
      result[key] = scrubValue(entry, depth + 1)
    }
    return result
  }
  return value
}

const scrubEvent = <T>(event: T): T => {
  const sanitized = scrubValue(event) as T
  const request = (sanitized as typeof event & { request?: Record<string, unknown> })?.request
  if (request) {
    request.headers = undefined
    request.cookies = undefined
    request.data = undefined
  }
  return sanitized
}

const parseSampleRate = (value: string | undefined, fallback: number): number => {
  if (!value) return fallback
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  if (parsed < 0) return 0
  if (parsed > 1) return 1
  return parsed
}

const tracesSampleRate = parseSampleRate(
  process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
  process.env.NODE_ENV === 'production' ? 0.1 : 1.0
)
const replaysSessionSampleRate = parseSampleRate(
  process.env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  process.env.NODE_ENV === 'production' ? 0.1 : 0
)
const replaysOnErrorSampleRate = parseSampleRate(
  process.env.NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  1.0
)

Sentry.init({
  dsn: SENTRY_DSN,
  enabled: !!SENTRY_DSN && process.env.NODE_ENV === 'production',
  environment: process.env.NODE_ENV,
  release: SENTRY_RELEASE,

  // Performance Monitoring
  tracesSampleRate,

  // Session Replay
  replaysSessionSampleRate,
  replaysOnErrorSampleRate,

  integrations: [
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
    }),
  ],

  // Filter out noise
  beforeSend(event) {
    const errorMessage = event.exception?.values?.[0]?.value ?? ''

    // Skip chunk load errors (cached old bundles)
    if (errorMessage.includes('ChunkLoadError') || errorMessage.includes('Loading chunk')) {
      return null
    }

    // Skip network errors that are expected
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
      return null
    }

    return scrubEvent(event)
  },

  beforeSendTransaction(event) {
    return scrubEvent(event)
  },

  // Ignore common browser errors
  ignoreErrors: [
    // Browser extensions
    'top.GLOBALS',
    'fb_xd_fragment',
    // Chrome errors
    'ResizeObserver loop',
    // Safari
    'cancelled',
  ],
})
